import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getFieldErrors, IFieldError } from "../helpers";
import { tryConfirmLogin } from "./authReducer";

export interface ISite {
  _id: string;
  owner: {
    type: string;
    id: string;
  };
  name: string;
  address: string;
  googleMapURL?: string;
}

class RegSiteErrs {
  form: string | null = null;
  name: string | null = null;
  address: string | null = null;
  googleMapURL: string | null = null;
}

export interface ISitesState {
  registeringSite: boolean;
  registeredSite: boolean;
  regSiteErrs: RegSiteErrs;
  fetchingSites: boolean;
  sites: ISite[];
  fetchSitesErr: string | null;
  updatingSite: boolean;
  updatedSite: boolean;
  updateSiteErrs: RegSiteErrs;
}

const initialState = {
  registeringSite: false,
  registeredSite: false,
  regSiteErrs: new RegSiteErrs(),
  fetchingSites: false,
  sites: [],
  fetchSitesErr: null,
  updatingSite: false,
  updatedSite: false,
  updateSiteErrs: new RegSiteErrs(),
} as ISitesState;

const sitesSlice = createSlice({
  name: "slices",
  initialState,
  reducers: {
    clearRegSiteFieldErr: (
      state: ISitesState,
      action: PayloadAction<string>,
    ) => {
      state.regSiteErrs[action.payload as keyof RegSiteErrs] = null;
    },
    clearRegSiteErrs: (state: ISitesState) => {
      state.regSiteErrs = new RegSiteErrs();
    },
    tryResetRegSiteFormErr: (state: ISitesState) => {
      let shouldReset = true;

      for (let key in state.regSiteErrs) {
        if (key === "form") {
          continue;
        }

        if (state.regSiteErrs[key as keyof RegSiteErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.regSiteErrs.form = null;
      }
    },
    resetRegisteredSite: (state: ISitesState) => {
      state.registeredSite = false;
    },
    regSiteStart: (state: ISitesState) => {
      state.regSiteErrs = new RegSiteErrs();
      state.registeredSite = false;
      state.registeringSite = true;
    },
    regSitePass: (state: ISitesState, action: PayloadAction<ISite>) => {
      state.registeredSite = true;
      // state.mainSite = action.payload;
      state.registeringSite = false;
    },
    regSiteFail: (state: ISitesState, action: PayloadAction<IFieldError[]>) => {
      state.registeringSite = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.regSiteErrs) {
          if (err.field === key) {
            state.regSiteErrs[key as keyof RegSiteErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.regSiteErrs.form) {
        state.regSiteErrs.form = "Form incorrectly filled.";
      }
    },
    clearFetchSitesErr: (state: ISitesState) => {
      state.fetchSitesErr = null;
    },
    fetchSitesStart: (state: ISitesState) => {
      state.fetchSitesErr = null;
      state.fetchingSites = true;
    },
    fetchSitesPass: (state: ISitesState, action: PayloadAction<ISite[]>) => {
      state.fetchingSites = false;
      state.sites = action.payload;
    },
    fetchSitesFail: (state: ISitesState, action: PayloadAction<string>) => {
      state.fetchingSites = false;
      state.fetchSitesErr = action.payload;
    },
    clearupdateSiteFieldErr: (
      state: ISitesState,
      action: PayloadAction<string>,
    ) => {
      state.updateSiteErrs[action.payload as keyof RegSiteErrs] = null;
    },
    clearUpdateSiteErrs: (state: ISitesState) => {
      state.updateSiteErrs = new RegSiteErrs();
    },
    tryResetUpdateSiteFormErr: (state: ISitesState) => {
      let shouldReset = true;

      for (let key in state.updateSiteErrs) {
        if (key === "form") {
          continue;
        }

        if (state.updateSiteErrs[key as keyof RegSiteErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.updateSiteErrs.form = null;
      }
    },
    resetUpdatedSite: (state: ISitesState) => {
      state.updatedSite = false;
    },
    updateSiteStart: (state: ISitesState) => {
      state.updatedSite = false;
      state.updateSiteErrs = new RegSiteErrs();
      state.updatingSite = true;
    },
    updateSitePass: (state: ISitesState, action: PayloadAction<ISite>) => {
      state.updatingSite = false;
      state.updatedSite = true;
    },
    updateSiteFail: (
      state: ISitesState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.updatingSite = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updateSiteErrs) {
          if (err.field === key) {
            state.updateSiteErrs[key as keyof RegSiteErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.updateSiteErrs.form) {
        state.updateSiteErrs.form = "Form incorrectly filled.";
      }
    },
  },
});

export const {
  clearRegSiteErrs,
  clearRegSiteFieldErr,
  tryResetRegSiteFormErr,
  resetRegisteredSite,
  regSiteStart,
  regSitePass,
  regSiteFail,
  clearFetchSitesErr,
  fetchSitesStart,
  fetchSitesPass,
  fetchSitesFail,
  clearUpdateSiteErrs,
  clearupdateSiteFieldErr,
  tryResetUpdateSiteFormErr,
  resetUpdatedSite,
  updateSiteStart,
  updateSitePass,
  updateSiteFail,
} = sitesSlice.actions;

interface ITryRegSiteArg {
  data: {
    ownerType: string;
    ownerId?: string;
    name: string;
    address: string;
    googleMapURL?: string;
  };
  authToken: string;
}

export const tryRegSite = createAsyncThunk(
  "sites/tryRegSiteArg",
  (arg: ITryRegSiteArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/site`;

    const { data, authToken } = arg;

    dispatch(regSiteStart());

    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(regSitePass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(regSiteFail(fieldErrors));
      });
  },
);

interface ITryFetchSitesArg {
  authToken: string;
  ownerType: string;
  ownerId?: string;
}

export const tryFetchSites = createAsyncThunk(
  "sites/tryFetchSites",
  (arg: ITryFetchSitesArg, { dispatch }) => {
    const { authToken, ownerId, ownerType } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/sites?ownerType=${ownerType}&ownerId=${ownerId}`;

    dispatch(fetchSitesStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(fetchSitesPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }
        let error = "";

        if (data?.err) {
          error = data.err;
        } else if (data?.errors) {
          error = getFieldErrors(data.errors)[0].msg;
        } else {
          error = "Check your network connection.";
        }

        dispatch(fetchSitesFail(error));
      });
  },
);

interface ITryUpdateSiteArg {
  siteId: string;
  data: {
    ownerType: string;
    ownerId: string;
    name?: string;
    address?: string;
    googleMapURL?: string;
  };
  authToken: string;
}

export const tryUpdateSite = createAsyncThunk(
  "sites/tryUpdateSite",
  (arg: ITryUpdateSiteArg, { dispatch }) => {
    const { siteId, data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/sites/${siteId}`;

    dispatch(updateSiteStart());

    axios
      .put(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(updateSitePass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(updateSiteFail(fieldErrors));
      });
  },
);

export default sitesSlice.reducer;
